import { useEffect, useMemo, useRef, useState } from 'react'
import { Check, ChevronsUpDown, X } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'

/**
 * Type-to-search multi-select.
 *
 * A native `<select>` stops being usable at a few dozen options, which is
 * exactly where the user, app and version filters land: there is no scrolling a
 * flat list to find one address among hundreds. This narrows as you type,
 * selects several at once, and shows what is selected without opening.
 */

export interface MultiSelectOption {
  value: string
  label: string
  /** Shown right-aligned — event counts, so the busiest options are obvious. */
  hint?: string
}

export interface MultiSelectProps {
  label: string
  placeholder: string
  options: MultiSelectOption[]
  selected: string[]
  onChange: (next: string[]) => void
  /** Width of the trigger. Defaults to a filter-bar-sized control. */
  className?: string
  loading?: boolean
}

export function MultiSelect({
  label,
  placeholder,
  options,
  selected,
  onChange,
  className = 'w-[14rem]',
  loading = false,
}: MultiSelectProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [highlight, setHighlight] = useState(0)
  const root = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return options
    // Match on both value and label: someone searching "atlantic" should find a
    // user by their address, and a share by its name.
    return options.filter(o =>
      o.value.toLowerCase().includes(q) || o.label.toLowerCase().includes(q))
  }, [options, search])

  // Close on an outside click or Escape — a filter popover that traps you is
  // worse than a plain select.
  useEffect(() => {
    if (!open) return
    const onPointer = (e: MouseEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  useEffect(() => {
    if (open) searchRef.current?.focus()
    else { setSearch(''); setHighlight(0) }
  }, [open])

  useEffect(() => { setHighlight(0) }, [search])

  const toggle = (value: string) => {
    onChange(selected.includes(value) ? selected.filter(v => v !== value) : [...selected, value])
  }

  const summary = selected.length === 0
    ? placeholder
    : selected.length === 1
      ? (options.find(o => o.value === selected[0])?.label ?? selected[0])
      : `${selected.length} selected`

  return (
    <div className="flex flex-col gap-1" ref={root}>
      <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</span>
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          aria-haspopup="listbox"
          aria-expanded={open}
          className={`flex h-9 items-center justify-between gap-2 rounded-md border border-input bg-background px-2 text-sm ${className}`}
        >
          <span className={`truncate ${selected.length === 0 ? 'text-muted-foreground' : 'text-foreground'}`}>
            {summary}
          </span>
          <span className="flex shrink-0 items-center gap-1">
            {selected.length > 0 && (
              <span
                role="button"
                tabIndex={0}
                aria-label={`Clear ${label}`}
                className="rounded p-0.5 hover:bg-muted"
                onClick={e => { e.stopPropagation(); onChange([]) }}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); onChange([]) } }}
              >
                <X className="size-3.5" />
              </span>
            )}
            <ChevronsUpDown className="size-3.5 opacity-50" />
          </span>
        </button>

        {open && (
          <div className="absolute z-50 mt-1 w-[min(22rem,90vw)] rounded-md border bg-popover shadow-md">
            <div className="border-b p-2">
              <Input
                ref={searchRef}
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={`Search ${label.toLowerCase()}…`}
                className="h-8"
                onKeyDown={e => {
                  if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => Math.min(h + 1, filtered.length - 1)) }
                  if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)) }
                  if (e.key === 'Enter' && filtered[highlight]) { e.preventDefault(); toggle(filtered[highlight].value) }
                }}
              />
            </div>

            <div className="flex items-center justify-between border-b px-2 py-1.5 text-xs">
              <span className="text-muted-foreground">
                {filtered.length} of {options.length}
                {selected.length > 0 && ` · ${selected.length} selected`}
              </span>
              <span className="flex gap-1">
                <Button
                  size="sm" variant="ghost" className="h-6 px-2 text-xs"
                  // Selects what is CURRENTLY visible, not the whole list —
                  // "select all" after a search should mean "all of these".
                  onClick={() => onChange([...new Set([...selected, ...filtered.map(o => o.value)])])}
                  disabled={filtered.length === 0}
                >
                  Select all
                </Button>
                <Button
                  size="sm" variant="ghost" className="h-6 px-2 text-xs"
                  onClick={() => onChange([])}
                  disabled={selected.length === 0}
                >
                  Clear
                </Button>
              </span>
            </div>

            <div className="max-h-64 overflow-y-auto py-1" role="listbox" aria-multiselectable>
              {loading && options.length === 0 && (
                <p className="px-3 py-6 text-center text-sm text-muted-foreground">Loading…</p>
              )}
              {!loading && filtered.length === 0 && (
                <p className="px-3 py-6 text-center text-sm text-muted-foreground">No matches</p>
              )}
              {filtered.map((option, i) => {
                const isSelected = selected.includes(option.value)
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    onMouseEnter={() => setHighlight(i)}
                    onClick={() => toggle(option.value)}
                    className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm ${
                      i === highlight ? 'bg-muted' : ''
                    }`}
                  >
                    <span className={`grid size-4 shrink-0 place-items-center rounded border ${
                      isSelected ? 'border-seed-green-500 bg-seed-green-500 text-white' : 'border-input'
                    }`}>
                      {isSelected && <Check className="size-3" />}
                    </span>
                    <span className="flex-1 truncate text-foreground">{option.label}</span>
                    {option.hint && (
                      <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{option.hint}</span>
                    )}
                  </button>
                )
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
